import { create } from "zustand";
import { useShallow } from "zustand/react/shallow";
import type {
  ImportEntryPoint,
  ImportSource,
  UploadSnapshot,
} from "./file-to-storydoc-generation.store";

export type FileToStorydocStep =
  | "source"
  | "uploading"
  | "selecting"
  | "choosing"
  | "error";

// Where the generated slides land.
//   "new-story" — the dashboard import: creates a fresh Storydoc.
//   "deck"      — the editor agent import: inserts the slides into the story
//                 that is already open, leaving its design system alone.
export type FileToStorydocTarget = "new-story" | "deck";

// The most pages one job converts (Yaacov 2026-09-17). A longer deck can still
// be uploaded; the user picks up to this many pages, and the message says so.
export const MAX_SELECTED_PAGES = 30;

const capSelection = (pages: PagePreview[]): PagePreview[] => {
  let left = MAX_SELECTED_PAGES;
  return pages.map((p) => {
    if (!p.selected) return p;
    if (left > 0) {
      left -= 1;
      return p;
    }
    return { ...p, selected: false };
  });
};

export interface PagePreview {
  pageNumber: number; // 1-based
  thumbnailUrl: string;
  selected: boolean;
}

interface IState {
  step: FileToStorydocStep;
  target: FileToStorydocTarget;
  // Set when the user presses Continue, so a dialog closed any other way can
  // be told apart from one that handed off to generation.
  continued: boolean;
  originalFile: File | null;
  pdfFile: File | null;
  pdfUrl: string | null;
  pages: PagePreview[];
  selectedDesignSystemId: string | null;
  // KEEP THE DECK'S OWN THEME. Distinct from `selectedDesignSystemId: null`,
  // which the runner reads as "no theme chosen — make it look like the FILE".
  // Both the deck-theme and the file-design choices left the id null, so the
  // two were indistinguishable downstream and a deck import always repainted
  // itself in the source's colours. Dawid: "when importing slides, it's always
  // taking source theme, even when I choose deck theme."
  keepDeckTheme: boolean;
  // The attachment was offered to the agent as "take the theme": run the
  // pipeline for its measurement of the source's design, then apply that and
  // insert NO slides.
  themeOnly: boolean;
  statusMessage: string;
  uploadProgress: number;
  returnedFromVersions: boolean;
  errorMsg: string | null;
  source: ImportSource;
  entryPoint: ImportEntryPoint;
  inlineError: string | null;
  inlineErrorRetry: "drive" | null;
}

interface IActions {
  setStep: (step: FileToStorydocStep) => void;
  setTarget: (target: FileToStorydocTarget) => void;
  markContinued: () => void;
  setOriginalFile: (file: File | null) => void;
  setPdfFile: (file: File | null) => void;
  setPdfUrl: (url: string | null) => void;
  setPages: (pages: PagePreview[]) => void;
  togglePage: (pageNumber: number) => void;
  setAllSelected: (selected: boolean) => void;
  setKeepDeckTheme: (keep: boolean) => void;
  setThemeOnly: (themeOnly: boolean) => void;
  setSelectedDesignSystemId: (id: string | null) => void;
  setStatusMessage: (msg: string) => void;
  setUploadProgress: (progress: number) => void;
  setReturnedFromVersions: (returned: boolean) => void;
  setError: (msg: string) => void;
  setSource: (source: ImportSource) => void;
  setEntryPoint: (entryPoint: ImportEntryPoint) => void;
  setInlineError: (msg: string | null, retry?: "drive") => void;
  restoreUpload: (upload: UploadSnapshot) => void;
  reset: () => void;
}

const initState: IState = {
  step: "source",
  target: "new-story",
  continued: false,
  originalFile: null,
  pdfFile: null,
  pdfUrl: null,
  pages: [],
  selectedDesignSystemId: null,
  keepDeckTheme: false,
  themeOnly: false,
  statusMessage: "Uploading your file...",
  uploadProgress: 0,
  returnedFromVersions: false,
  errorMsg: null,
  source: "local",
  entryPoint: "unknown",
  inlineError: null,
  inlineErrorRetry: null,
};

export const useFileToStorydocStore = create<IState & IActions>((set) => ({
  ...initState,
  setStep: (step) => set({ step }),
  setTarget: (target) => set({ target }),
  markContinued: () => set({ continued: true }),
  setOriginalFile: (originalFile) => set({ originalFile }),
  setPdfFile: (pdfFile) => set({ pdfFile }),
  setPdfUrl: (pdfUrl) => set({ pdfUrl }),
  setPages: (pages) => set({ pages: capSelection(pages) }),
  togglePage: (pageNumber) =>
    set((state) => {
      const target = state.pages.find((p) => p.pageNumber === pageNumber);
      if (!target) return {};
      const selectedCount = state.pages.filter((p) => p.selected).length;
      // at the cap, selecting one more is refused (the message in the step says why)
      if (!target.selected && selectedCount >= MAX_SELECTED_PAGES) return {};
      return {
        pages: state.pages.map((p) =>
          p.pageNumber === pageNumber ? { ...p, selected: !p.selected } : p,
        ),
      };
    }),
  // "Select all" selects the first MAX_SELECTED_PAGES on a longer deck.
  setAllSelected: (selected) =>
    set((state) => ({
      pages: capSelection(state.pages.map((p) => ({ ...p, selected }))),
    })),
  setKeepDeckTheme: (keepDeckTheme) => set({ keepDeckTheme }),
  setThemeOnly: (themeOnly) => set({ themeOnly }),
  setSelectedDesignSystemId: (selectedDesignSystemId) =>
    set({ selectedDesignSystemId }),
  setStatusMessage: (statusMessage) => set({ statusMessage }),
  setReturnedFromVersions: (returnedFromVersions) =>
    set({ returnedFromVersions }),
  setUploadProgress: (uploadProgress) =>
    set({ uploadProgress: Math.min(1, Math.max(0, uploadProgress)) }),
  setError: (errorMsg) => set({ step: "error", errorMsg }),
  setSource: (source) => set({ source }),
  setEntryPoint: (entryPoint) => set({ entryPoint }),
  setInlineError: (inlineError, retry) =>
    set({
      inlineError,
      inlineErrorRetry: inlineError ? (retry ?? null) : null,
    }),
  restoreUpload: (u) =>
    set({
      originalFile: u.originalFile,
      pdfFile: u.pdfFile,
      pdfUrl: u.pdfUrl,
      pages: u.pages,
      selectedDesignSystemId: u.selectedDesignSystemId,
      source: u.source,
      step: "selecting",
      returnedFromVersions: true,
      errorMsg: null,
    }),
  reset: () => set({ ...initState }),
}));

export const useFileToStorydocStoreShallow = <U>(
  selector: (store: IState & IActions) => U,
) => useFileToStorydocStore(useShallow(selector));

export const selectSelectedPageNumbers = (pages: PagePreview[]) =>
  pages.filter((p) => p.selected).map((p) => p.pageNumber);
